import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Package, Loader2, Search, ChevronDown, ChevronRight, Tag as TagIcon } from "lucide-react";
import api from "../lib/api";
import { fmtDate } from "../lib/permissions";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

function ProductForm({ open, onClose, initial, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState("");
  const [codesText, setCodesText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setCodesText("");
    }
  }, [initial, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name cannot be empty"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const r = await api.put(`/products/${initial.id}`, { name });
        const n = r.data?.propagated_to_contacts || 0;
        toast.success(n > 0
          ? `Product renamed — updated on ${n} existing contact${n === 1 ? "" : "s"}`
          : "Product renamed");
      } else {
        const codes = codesText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
        await api.post("/products", { name, codes });
        toast.success(`Product added${codes.length ? ` with ${codes.length} code${codes.length === 1 ? "" : "s"}` : ""}`);
      }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Rename Product" : "Add New Product"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Product Name</Label>
            <Input data-testid="product-name-input" required value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Natural Vanilla Flavor" autoFocus />
            {isEdit && (
              <p className="text-xs text-muted-foreground mt-1">
                Renaming will automatically update this product name on every contact that currently uses it.
              </p>
            )}
          </div>
          {!isEdit && (
            <div>
              <Label>Product Codes <span className="text-muted-foreground">(optional — one per line or comma-separated)</span></Label>
              <Textarea data-testid="product-codes-input" rows={4} value={codesText}
                onChange={(e) => setCodesText(e.target.value)}
                placeholder={"SNF-VAN-01\nSNF-VAN-02\nSNF-VAN-ORG"} />
              <p className="text-xs text-muted-foreground mt-1">
                Codes distinguish different grades of the same product. You can add or edit codes later by expanding the product row.
              </p>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="product-save" disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Save Changes" : "Add Product")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CodeRow({ product, code, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(code.code);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!val.trim() || val.trim() === code.code) { setEditing(false); setVal(code.code); return; }
    setBusy(true);
    try {
      const r = await api.put(`/products/${product.id}/codes/${code.id}`, { code: val.trim() });
      const n = r.data?.propagated_to_contacts || 0;
      toast.success(n > 0 ? `Code renamed — updated on ${n} contact${n === 1 ? "" : "s"}` : "Code renamed");
      setEditing(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); setVal(code.code); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm(`Remove code "${code.code}"? Existing contacts using it will keep their historical value.`)) return;
    try {
      await api.delete(`/products/${product.id}/codes/${code.id}`);
      toast.success("Code removed");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="flex items-center gap-2 py-1.5 group">
      {editing ? (
        <>
          <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()}
            className="h-8 text-sm font-mono flex-1" autoFocus />
          <Button size="sm" variant="ghost" onClick={save} disabled={busy} className="h-8 px-2 text-primary">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setVal(code.code); }} className="h-8 px-2">Cancel</Button>
        </>
      ) : (
        <>
          <Badge variant="secondary" className="font-mono text-xs px-2.5 py-1">{code.code}</Badge>
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}
            data-testid={`edit-code-${code.id}`}
            className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <Pencil className="w-3 h-3" />
          </Button>
          <Button size="sm" variant="ghost" onClick={remove}
            data-testid={`delete-code-${code.id}`}
            className="h-7 w-7 p-0 text-destructive opacity-0 group-hover:opacity-100 transition-opacity">
            <Trash2 className="w-3 h-3" />
          </Button>
        </>
      )}
    </div>
  );
}

function CodesPanel({ product, onRefresh }) {
  const [newCode, setNewCode] = useState("");
  const [adding, setAdding] = useState(false);
  const codes = product.codes || [];

  const add = async (e) => {
    e.preventDefault();
    if (!newCode.trim()) return;
    setAdding(true);
    try {
      await api.post(`/products/${product.id}/codes`, { code: newCode.trim() });
      toast.success("Code added");
      setNewCode("");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setAdding(false); }
  };

  return (
    <div className="bg-secondary/40 px-8 py-4 border-y border-border">
      <div className="flex items-center gap-2 mb-2">
        <TagIcon className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
          Product Codes {codes.length > 0 && <span className="text-primary">({codes.length})</span>}
        </span>
      </div>
      {codes.length > 0 ? (
        <div className="space-y-0 max-w-lg">
          {codes.map((c) => <CodeRow key={c.id} product={product} code={c} onRefresh={onRefresh} />)}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic mb-2">No codes yet. Add codes to distinguish grades of {product.name}.</div>
      )}
      <form onSubmit={add} className="flex items-center gap-2 mt-3 max-w-lg">
        <Input data-testid={`new-code-input-${product.id}`}
          placeholder="Add code (e.g. SNF-VAN-01)" value={newCode} onChange={(e) => setNewCode(e.target.value)}
          className="h-8 text-sm font-mono" />
        <Button type="submit" size="sm" data-testid={`add-code-btn-${product.id}`}
          disabled={adding || !newCode.trim()} className="bg-primary hover:bg-primary/90 text-white h-8">
          {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" />Add Code</>}
        </Button>
      </form>
    </div>
  );
}

export default function ManageProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [usageCount, setUsageCount] = useState(0);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/products");
      setProducts(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDelete = async (p) => {
    setDeleting(p);
    try {
      const r = await api.get("/contacts", { params: { product: p.name } });
      setUsageCount(r.data.filter((c) => c.product_name === p.name).length);
    } catch { setUsageCount(0); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/products/${deleting.id}`);
      toast.success(`${deleting.name} removed from master`);
      setDeleting(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
    finally { setDeleteBusy(false); }
  };

  const filtered = products.filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-2"><Package className="w-6 h-6 text-primary" />Manage Products</h1>
          <p className="text-sm text-muted-foreground">Central catalog of ingredients. Each product can have multiple codes for its different grades. Sales Staff pick from the linked dropdowns when creating a contact.</p>
        </div>
        <Button data-testid="new-product-btn" onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-primary hover:bg-primary/90 text-white">
          <Plus className="w-4 h-4 mr-2" />Add Product
        </Button>
      </div>

      <div className="bg-white rounded-md border border-border p-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="product-search" placeholder="Search products…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
      </div>

      <div className="bg-white rounded-md border border-border overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-primary" /></div>
        ) : (
          <div data-testid="products-list">
            <div className="bg-secondary/60 text-[11px] uppercase tracking-widest text-muted-foreground grid grid-cols-[24px_1fr_120px_140px_100px] gap-4 px-5 py-3">
              <div></div>
              <div>Product Name</div>
              <div>Codes</div>
              <div>Added</div>
              <div className="text-right">Actions</div>
            </div>
            {filtered.map((p) => {
              const isOpen = !!expanded[p.id];
              return (
                <div key={p.id} className="border-t border-border">
                  <div className="grid grid-cols-[24px_1fr_120px_140px_100px] gap-4 px-5 py-3 items-center hover:bg-neutral-50">
                    <button data-testid={`expand-${p.id}`} onClick={() => setExpanded({ ...expanded, [p.id]: !isOpen })}
                      className="w-6 h-6 rounded hover:bg-neutral-100 flex items-center justify-center text-muted-foreground">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <div className="font-semibold">{p.name}</div>
                    <div>
                      <Badge variant="secondary" className="text-xs">{(p.codes || []).length} code{(p.codes || []).length === 1 ? "" : "s"}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">{fmtDate(p.created_at)}</div>
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`edit-product-${p.id}`} size="sm" variant="ghost"
                        onClick={() => { setEditing(p); setFormOpen(true); }} title="Rename">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button data-testid={`delete-product-${p.id}`} size="sm" variant="ghost"
                        onClick={() => openDelete(p)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  {isOpen && <CodesPanel product={p} onRefresh={load} />}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="text-center py-10 text-sm text-muted-foreground">
                {search ? "No products match your search." : "No products yet. Add your first one to populate the Contacts dropdown."}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-secondary/50 border border-border rounded-md p-4 text-xs text-muted-foreground space-y-1">
        <div className="font-semibold text-neutral-800">How it works</div>
        <div>• Click the arrow on any row to expand and manage that product's codes.</div>
        <div>• Sales Staff first pick a Product Name in the Contacts form — the Product Code dropdown then shows only codes belonging to that product.</div>
        <div>• Renaming a product or a code auto-updates every contact that references the old value. Deleting keeps historical values on existing contacts intact.</div>
      </div>

      <ProductForm open={formOpen} onClose={() => setFormOpen(false)} initial={editing} onSaved={load} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this product?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to delete <span className="font-semibold text-neutral-900">{deleting?.name}</span> from the product master along with its {(deleting?.codes || []).length} associated code{(deleting?.codes || []).length === 1 ? "" : "s"}.
              {usageCount > 0 && (
                <span className="block mt-2 text-orange-800 bg-orange-50 border border-orange-200 rounded p-2 text-xs">
                  <b>{usageCount}</b> existing contact{usageCount === 1 ? "" : "s"} currently reference this product. Their historical values will remain intact, but this product will no longer appear in the dropdown for new contacts.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="product-delete-confirm" onClick={confirmDelete} disabled={deleteBusy}
              className="bg-destructive text-white hover:bg-destructive/90">
              {deleteBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
